import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";
import { ProgressSpinnerModule } from "primeng/progressspinner";

// Our interfaces
import {
  BulkActionsResponse,
  BulkDetailedResponseDialogData,
} from "../../interfaces/bulk-actions-response.interface";

/**
 * Bulk Detailed Response Dialog Component
 * Shows detailed results of bulk operations on scheduled orders
 * Matches old QMC BulkDetailedResponseDialog functionality
 */
@Component({
  selector: "app-bulk-detailed-response-dialog",
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    TagModule,
    ProgressSpinnerModule,
  ],
  templateUrl: "./bulk-detailed-response-dialog.component.html",
  styleUrl: "./bulk-detailed-response-dialog.component.scss",
})
export class BulkDetailedResponseDialogComponent {
  data: BulkDetailedResponseDialogData;
  responses: BulkActionsResponse[] = [];
  statusFilter: "all" | "success" | "error" = "all";

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig<BulkDetailedResponseDialogData>,
    private router: Router,
  ) {
    this.data = this.config.data!;
    this.responses = this.data.response || [];
  }

  /**
   * Get filtered responses based on status filter
   */
  get filteredResponses(): BulkActionsResponse[] {
    if (this.statusFilter === "all") {
      return this.responses;
    } else if (this.statusFilter === "success") {
      return this.responses.filter((r) => !r.Failed);
    } else {
      return this.responses.filter((r) => r.Failed);
    }
  }

  /**
   * Toggle status filter
   */
  toggleFilter(filter: "all" | "success" | "error"): void {
    // If clicking the same filter, reset to 'all'
    if (this.statusFilter === filter) {
      this.statusFilter = "all";
    } else {
      this.statusFilter = filter;
    }
  }

  /**
   * Close the dialog
   */
  onClose(): void {
    this.ref.close();
  }

  /**
   * Get severity for the result tag
   */
  getResultSeverity(failed: boolean): "success" | "danger" {
    return failed ? "danger" : "success";
  }

  /**
   * Get result text
   */
  getResultText(failed: boolean): string {
    return failed ? "Error" : "Success";
  }

  /**
   * Navigate to scheduled order edit page
   */
  goToScheduledOrderEdit(scheduledOrderId: number): void {
    const url = `/widgets/dashboard/${this.data.siteId}/scheduled-orders/${scheduledOrderId}/edit`;
    void this.router.navigate([url]);
    this.onClose();
  }

  /**
   * Get summary stats for display
   */
  getSummaryStats(): { total: number; succeeded: number; failed: number } {
    const total = this.responses.length;
    const failed = this.responses.filter((r) => r.Failed).length;
    const succeeded = total - failed;

    return { total, succeeded, failed };
  }

  /**
   * Track by function for ngFor performance
   */
  trackByResponseId(index: number, response: BulkActionsResponse): number {
    return response.ScheduledOrderId;
  }
}
